import { useEffect, useMemo } from "react";
import { useInfiniteQuery, useQuery } from "react-query";
import {
  fetchTemplateShopCategoryTabs,
  fetchTemplateShopProducts,
  fetchTemplateTrendingShopProducts,
  fetchTemplateShopProductsRpcPage,
  fetchTemplateShopContentRpcPage,
  findShopCategoryLabelBySlug,
  browseIndexFromProductsFacets,
  categoryTabsFromProductsFacets,
  TEMPLATE_SHOP_PAGE_SIZE,
  type ShopCatalogProduct,
  type ShopCatalogContent,
  type ShopCategoryTab,
  type TemplateShopView,
  type ShopProductsFacets,
  type ShopContentNavItem,
} from "./templateShopCatalog";
import {
  browseSelectionIsActive,
  buildShopBrowseIndex,
  countBrowseSelectionProducts,
  countProductsByBrand,
  coerceShopBrowseSelection,
  findBrowseNavCount,
  resolveShopBrowseLabels,
  SHOP_BROWSE_OTHER_SLUG,
  type ShopBrowseIndex,
  type ShopBrowseSelection,
} from "./shopBreadcrumbs";

export type ShopCatalogSurface = "products" | "content" | "search" | "meta";

function resolveSurface(
  surface: ShopCatalogSurface | undefined,
  shopView: TemplateShopView,
): ShopCatalogSurface {
  if (surface) return surface;
  if (shopView === "content") return "content";
  return "products";
}

function nextOffsetFromPages(
  lastPage: { pageSize: number; products?: unknown[]; items?: unknown[]; total: number },
  allPages: { products?: unknown[]; items?: unknown[] }[],
): number | undefined {
  const loaded = allPages.reduce(
    (n, p) => n + (p.products?.length ?? p.items?.length ?? 0),
    0,
  );
  const lastCount = lastPage.products?.length ?? lastPage.items?.length ?? 0;
  if (lastCount < lastPage.pageSize) return undefined;
  if (loaded >= lastPage.total) return undefined;
  return loaded;
}

/**
 * Template Product / Content catalog.
 * Network pages are demand-driven (IntersectionObserver → fetchNextPage).
 * Facets come from a dedicated first-page RPC so nav counts do not require a full drain.
 */
export function useShopCatalog(params: {
  username: string;
  userId: string;
  enabled: boolean;
  shopView: TemplateShopView;
  categorySlug: string | null;
  shopBrowse: ShopBrowseSelection;
  shoppableFilter?: "all" | "yes" | "no";
  surface?: ShopCatalogSurface;
  searchQuery?: string | null;
  /**
   * Skip the paginated products/content grid fetch.
   * Used when a home-carousel collection is showing instead of the browse grid.
   */
  skipGrid?: boolean;
  /**
   * Also skip facet/nav RPCs. Use only when the URL is a true collection
   * (not a browse-primary remap that still needs facets to classify the slug).
   */
  skipFacets?: boolean;
}) {
  const {
    username,
    userId,
    enabled,
    shopView,
    categorySlug,
    shopBrowse,
    shoppableFilter = "all",
    searchQuery = null,
    skipGrid = false,
    skipFacets = false,
  } = params;
  const surface = resolveSurface(params.surface, shopView);
  const on = enabled && Boolean(username.trim() && userId.trim());
  const wantProducts =
    on && (surface === "products" || surface === "search" || surface === "meta");
  const wantContent = on && (surface === "content" || surface === "search");
  const needle = String(searchQuery ?? "").trim();
  const browse = coerceShopBrowseSelection(shopBrowse);
  const brandSlugsKey = (shopBrowse.brandSlugs || []).join(",");
  const contentPlatformSlug = (shopBrowse.primarySlug ?? "").trim().toLowerCase();
  const contentFormatSlug = (shopBrowse.secondarySlug ?? "").trim().toLowerCase();

  /** Unfiltered first page — facets + category tabs + has-products probe. */
  const facetsQuery = useQuery(
    ["template-shop-products-facets-rpc-v1", userId, username],
    () =>
      fetchTemplateShopProductsRpcPage({
        username,
        userId,
        offset: 0,
        // Facets-only: avoid downloading a full grid page in parallel with productsQuery.
        limit: 1,
        includeFacets: true,
      }),
    {
      enabled: wantProducts && !skipFacets,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );

  const legacyCategoryTabsQuery = useQuery(
    ["template-shop-category-tabs-rpc-v1", userId, username],
    () => fetchTemplateShopCategoryTabs({ username, userId }),
    {
      enabled: wantProducts && !skipFacets && !facetsQuery.data?.facets?.categories?.length,
      staleTime: 60_000,
    },
  );

  const trendingQuery = useQuery(
    ["template-shop-trending-rpc-v1", userId, username],
    () => fetchTemplateTrendingShopProducts({ username, userId }),
    {
      enabled: wantProducts && (shopView === "trending" || surface === "meta"),
      staleTime: 60_000,
      keepPreviousData: true,
    },
  );

  const allProductsProbeQuery = useQuery(
    ["template-shop-all-probe-rpc-v1", userId, username],
    () => fetchTemplateShopProducts({ username, userId, limit: 1 }),
    {
      enabled: wantProducts && surface === "meta",
      staleTime: 60_000,
    },
  );

  const productsFacets: ShopProductsFacets | null =
    facetsQuery.data?.facets ?? null;

  const categoryTabs: ShopCategoryTab[] = useMemo(() => {
    const fromFacets = categoryTabsFromProductsFacets(productsFacets);
    if (fromFacets.length) return fromFacets;
    return legacyCategoryTabsQuery.data ?? [];
  }, [productsFacets, legacyCategoryTabsQuery.data]);

  const categoryLabel = useMemo(() => {
    if (shopView !== "category" || !categorySlug?.trim()) return null;
    return findShopCategoryLabelBySlug(categoryTabs, categorySlug);
  }, [shopView, categorySlug, categoryTabs]);

  const gridEnabled =
    wantProducts &&
    !skipGrid &&
    surface !== "meta" &&
    shopView !== "trending" &&
    shopView !== "content";

  const productsQuery = useInfiniteQuery(
    [
      "template-shop-products-rpc-v1",
      userId,
      username,
      shopView,
      categoryLabel,
      browse.primarySlug,
      browse.secondarySlug,
      browse.subSlug,
      browse.brandSlug,
      brandSlugsKey,
      needle,
    ],
    ({ pageParam = 0 }) => {
      if (shopView === "brand") {
        const multi = (shopBrowse.brandSlugs || [])
          .map((s) => String(s || "").trim().toLowerCase())
          .filter(Boolean);
        const single = String(shopBrowse.brandSlug ?? "").trim().toLowerCase();
        return fetchTemplateShopProductsRpcPage({
          username,
          userId,
          offset: pageParam,
          limit: TEMPLATE_SHOP_PAGE_SIZE,
          brandSlugs: multi.length ? multi : single ? [single] : null,
          query: needle || null,
          includeFacets: false,
        });
      }
      return fetchTemplateShopProductsRpcPage({
        username,
        userId,
        offset: pageParam,
        limit: TEMPLATE_SHOP_PAGE_SIZE,
        contentCategory: shopView === "category" ? categoryLabel : null,
        primarySlug: browse.primarySlug || null,
        secondarySlug: browse.secondarySlug || null,
        subSlug: browse.subSlug || null,
        brandSlug: browse.brandSlug || null,
        brandSlugs: shopBrowse.brandSlugs,
        query: needle || null,
        includeFacets: false,
      });
    },
    {
      enabled:
        gridEnabled &&
        (shopView !== "category" || Boolean(categoryLabel?.trim())) &&
        (shopView !== "brand" ||
          Boolean(
            String(shopBrowse.brandSlug ?? "").trim() ||
              (shopBrowse.brandSlugs || []).length,
          )),
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      // Keep the previous cache entry mounted so the query identity stays
      // stable. The grid itself blanks to the skeleton while isPreviousData.
      keepPreviousData: true,
      getNextPageParam: (lastPage, allPages) =>
        nextOffsetFromPages(lastPage, allPages),
    },
  );

  /** Unfiltered facets — platform / format nav stays stable across filter changes. */
  const contentFacetsQuery = useQuery(
    ["template-shop-content-facets-rpc-v1", userId, username],
    () =>
      fetchTemplateShopContentRpcPage({
        username,
        userId,
        offset: 0,
        // Facets-only: avoid downloading a full grid page in parallel with contentQuery.
        limit: 1,
        includeFacets: true,
      }),
    {
      enabled: wantContent && !skipFacets,
      // Long-lived: Content page stays pre-mounted; avoid ribbon/grid reloads
      // when navigating away and back from other template pages.
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  );

  const contentQuery = useInfiniteQuery(
    [
      // v2: shoppable=yes is shop_link-only (not external/tagged).
      "template-shop-content-rpc-v2",
      userId,
      username,
      contentPlatformSlug,
      contentFormatSlug,
      shoppableFilter,
      needle,
    ],
    ({ pageParam = 0 }) =>
      fetchTemplateShopContentRpcPage({
        username,
        userId,
        offset: pageParam,
        limit: TEMPLATE_SHOP_PAGE_SIZE,
        platformSlug: contentPlatformSlug || null,
        formatSlug: contentFormatSlug || null,
        shoppable: shoppableFilter,
        query: needle || null,
        // Facets come from contentFacetsQuery — avoid coupling nav to grid keys.
        includeFacets: false,
      }),
    {
      enabled: wantContent && !skipGrid,
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      keepPreviousData: true,
      getNextPageParam: (lastPage, allPages) =>
        nextOffsetFromPages(lastPage, allPages),
    },
  );

  // Warm one page ahead so scroll-in tiles already have rows (not a full drain).
  useEffect(() => {
    if (!gridEnabled || shopView === "trending") return;
    if (productsQuery.isPreviousData) return;
    if (!productsQuery.hasNextPage || productsQuery.isFetchingNextPage) return;
    const pages = productsQuery.data?.pages?.length ?? 0;
    if (pages > 1) return;
    void productsQuery.fetchNextPage();
  }, [
    gridEnabled,
    shopView,
    productsQuery.hasNextPage,
    productsQuery.isFetchingNextPage,
    productsQuery.isPreviousData,
    productsQuery.data?.pages?.length,
    productsQuery.fetchNextPage,
  ]);

  useEffect(() => {
    if (!wantContent || skipGrid) return;
    if (contentQuery.isPreviousData) return;
    if (!contentQuery.hasNextPage || contentQuery.isFetchingNextPage) return;
    const pages = contentQuery.data?.pages?.length ?? 0;
    if (pages > 1) return;
    void contentQuery.fetchNextPage();
  }, [
    wantContent,
    skipGrid,
    contentQuery.hasNextPage,
    contentQuery.isFetchingNextPage,
    contentQuery.isPreviousData,
    contentQuery.data?.pages?.length,
    contentQuery.fetchNextPage,
  ]);

  const browseIndex: ShopBrowseIndex = useMemo(
    () => browseIndexFromProductsFacets(productsFacets),
    [productsFacets],
  );

  const categoryFilteredBrowseIndex = browseIndex;

  const hasBreadcrumbsColumn = Boolean(
    facetsQuery.data?.hasBreadcrumbsColumn ?? false,
  );
  const hasRealBreadcrumbPrimaries = browseIndex.primaries.some(
    (p) => p.slug !== SHOP_BROWSE_OTHER_SLUG,
  );
  const hasBrowseCatalog = hasBreadcrumbsColumn && hasRealBreadcrumbPrimaries;

  const isBrowseCatalogSettled = !facetsQuery.isLoading || Boolean(facetsQuery.data);

  const loadedProducts: ShopCatalogProduct[] = useMemo(() => {
    if (shopView === "trending") return trendingQuery.data?.products ?? [];
    const pages = productsQuery.data?.pages ?? [];
    const seen = new Set<string>();
    const out: ShopCatalogProduct[] = [];
    for (const page of pages) {
      for (const product of page.products) {
        if (seen.has(product.postId)) continue;
        seen.add(product.postId);
        out.push(product);
      }
    }
    return out;
  }, [shopView, trendingQuery.data?.products, productsQuery.data]);

  const allContent: ShopCatalogContent[] = useMemo(() => {
    const seen = new Set<string>();
    const out: ShopCatalogContent[] = [];
    for (const page of contentQuery.data?.pages ?? []) {
      for (const item of page.items) {
        if (seen.has(item.postId)) continue;
        seen.add(item.postId);
        out.push(item);
      }
    }
    return out;
  }, [contentQuery.data]);

  // Ignore keepPreviousData rows from the prior platform/format — they would
  // falsely keep the Shoppable tab visible while the next key loads.
  const contentGridIsCurrent = !contentQuery.isPreviousData;

  /** Fast path: current grid already proves at least one shoppable post. */
  const hasShoppableFromLoaded = useMemo(
    () =>
      contentGridIsCurrent && allContent.some((item) => item.shoppable),
    [allContent, contentGridIsCurrent],
  );

  /**
   * Cheap limit-1 probe so the Shoppable tab can hide when a platform has
   * zero shoppable posts. Skipped when the open grid already shows one
   * (or the user is already on the shoppable filter — that query is enough).
   */
  const shoppableProbeQuery = useQuery(
    [
      "template-shop-content-shoppable-probe-v1",
      userId,
      username,
      contentPlatformSlug,
      contentFormatSlug,
    ],
    () =>
      fetchTemplateShopContentRpcPage({
        username,
        userId,
        offset: 0,
        limit: 1,
        platformSlug: contentPlatformSlug || null,
        formatSlug: contentFormatSlug || null,
        shoppable: "yes",
        includeFacets: false,
      }),
    {
      enabled:
        wantContent &&
        !skipFacets &&
        Boolean(contentPlatformSlug) &&
        !hasShoppableFromLoaded &&
        shoppableFilter !== "yes",
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  );

  const hasShoppableContent =
    hasShoppableFromLoaded ||
    (shoppableFilter === "yes" &&
      contentGridIsCurrent &&
      ((contentQuery.data?.pages?.[0]?.total ?? 0) > 0 ||
        allContent.length > 0)) ||
    (shoppableProbeQuery.data?.total ?? 0) > 0 ||
    (shoppableProbeQuery.data?.items.length ?? 0) > 0;

  const emptyContentIndex = useMemo(
    () => ({
      platforms: [] as ShopContentNavItem[],
      formatsByPlatform: {} as Record<string, ShopContentNavItem[]>,
    }),
    [],
  );

  const contentIndex = useMemo(() => {
    const facets = contentFacetsQuery.data;
    if (
      facets &&
      (facets.platforms.length > 0 ||
        Object.keys(facets.formatsByPlatform).length > 0)
    ) {
      return {
        platforms: facets.platforms,
        formatsByPlatform: facets.formatsByPlatform,
      };
    }
    // Fallback: older cache entries that still carried facets on page 0.
    const first = contentQuery.data?.pages?.[0];
    if (
      first &&
      (first.platforms.length > 0 ||
        Object.keys(first.formatsByPlatform).length > 0)
    ) {
      return {
        platforms: first.platforms,
        formatsByPlatform: first.formatsByPlatform,
      };
    }
    return emptyContentIndex;
  }, [contentFacetsQuery.data, contentQuery.data, emptyContentIndex]);

  const isContentFacetsSettled =
    !contentFacetsQuery.isLoading || Boolean(contentFacetsQuery.data);

  const contentItems = allContent;

  const browseLabels = useMemo(
    () => resolveShopBrowseLabels(browseIndex, shopBrowse, loadedProducts),
    [browseIndex, shopBrowse, loadedProducts],
  );

  const brandCounts = useMemo(
    () => countProductsByBrand(loadedProducts),
    [loadedProducts],
  );

  const products: ShopCatalogProduct[] =
    shopView === "content" ? [] : loadedProducts;

  const productsTotal =
    shopView === "trending"
      ? products.length
      : productsQuery.data?.pages?.[0]?.total ?? products.length;

  const contentTotal =
    contentQuery.data?.pages?.[0]?.total ?? contentItems.length;

  const hasAllProducts =
    (allProductsProbeQuery.data?.products.length ?? 0) > 0 ||
    (facetsQuery.data?.total ?? 0) > 0 ||
    products.length > 0 ||
    browseIndex.primaries.length > 0;

  const hasTrending = (trendingQuery.data?.products.length ?? 0) > 0;

  // First-page fetch for a new filter key (not infinite-scroll next page).
  // A category, subcategory, platform, or shoppable switch must show the
  // skeleton instead of holding the previous grid until the new rows blink in.
  // keepPreviousData still fills `data` during that fetch, so treat
  // isPreviousData as loading. Cached keys that are not fetching stay instant.
  const productsFilterLoading =
    productsQuery.isFetching &&
    !productsQuery.isFetchingNextPage &&
    (Boolean(productsQuery.isPreviousData) ||
      !(productsQuery.data?.pages?.length));
  const contentFilterLoading =
    contentQuery.isFetching &&
    !contentQuery.isFetchingNextPage &&
    (Boolean(contentQuery.isPreviousData) ||
      !(contentQuery.data?.pages?.length));

  const productsForUi: ShopCatalogProduct[] = productsFilterLoading
    ? []
    : products;
  const contentItemsForUi: ShopCatalogContent[] = contentFilterLoading
    ? []
    : contentItems;

  const loading =
    shopView === "content"
      ? contentFilterLoading
      : shopView === "trending"
        ? trendingQuery.isLoading ||
          (trendingQuery.isFetching && !trendingQuery.data)
        : shopView === "category"
          ? (facetsQuery.isLoading && categoryTabs.length === 0) ||
            (Boolean(categoryLabel) && productsFilterLoading) ||
            // Category slug chosen but label not resolved yet from facets.
            (Boolean(categorySlug?.trim()) &&
              !categoryLabel &&
              (facetsQuery.isLoading ||
                legacyCategoryTabsQuery.isLoading ||
                !categoryTabs.length))
          : productsFilterLoading;

  const selectionProductCount = useMemo(() => {
    if (!browseSelectionIsActive(shopBrowse)) return productsTotal;
    const fromNav = findBrowseNavCount(browseIndex, shopBrowse);
    if (fromNav != null) return fromNav;
    return countBrowseSelectionProducts(loadedProducts, shopBrowse) || productsTotal;
  }, [shopBrowse, browseIndex, loadedProducts, productsTotal]);

  // Live product images win over RPC facet samples so Featured Categories /
  // Collections covers track the latest post in the loaded catalog.
  const primaryCoverBySlug = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of loadedProducts) {
      const slug = String(p.breadcrumbs?.primarySlug || "").trim();
      const img = String(p.image || "").trim();
      if (!slug || !img || map.has(slug)) continue;
      map.set(slug, img);
    }
    for (const p of productsFacets?.primaries ?? []) {
      const slug = String(p.slug || "").trim();
      const img = String(p.sample_image || "").trim();
      if (slug && img && !map.has(slug)) map.set(slug, img);
    }
    return map;
  }, [productsFacets, loadedProducts]);

  /** Sample covers for brand tiles (Featured collections / Featured brands). */
  const brandCoverBySlug = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of loadedProducts) {
      const slug = String(p.breadcrumbs?.brandSlug || "").trim().toLowerCase();
      const img = String(p.image || "").trim();
      if (!slug || !img || map.has(slug)) continue;
      map.set(slug, img);
    }
    for (const items of Object.values(productsFacets?.brandsByPathSlug ?? {})) {
      for (const b of items) {
        const slug = String(b.slug || "").trim().toLowerCase();
        const img = String(b.sample_image || "").trim();
        if (slug && img && !map.has(slug)) map.set(slug, img);
      }
    }
    return map;
  }, [productsFacets, loadedProducts]);

  return {
    products: productsForUi,
    categoryTabs,
    categoryLabel,
    browseIndex,
    browsePool: loadedProducts,
    categoryFilteredBrowsePool: loadedProducts,
    categoryFilteredBrowseIndex,
    hasBrowseCatalog,
    hasBreadcrumbsColumn,
    isBrowseCatalogSettled,
    browseLabels,
    selectionProductCount,
    hasAllProducts,
    hasTrending,
    loading,
    brandCounts,
    contentItems: contentItemsForUi,
    allContent,
    contentIndex,
    contentLoading: contentFilterLoading,
    isContentFacetsSettled,
    /** True when this platform/format has ≥1 shoppable post (for Content tab chrome). */
    hasShoppableContent,
    catalogProducts: loadedProducts,
    productsTotal,
    contentTotal,
    primaryCoverBySlug,
    brandCoverBySlug,
    fetchNextProductsPage: productsQuery.fetchNextPage,
    hasNextProductsPage: Boolean(productsQuery.hasNextPage),
    isFetchingNextProductsPage: productsQuery.isFetchingNextPage,
    fetchNextContentPage: contentQuery.fetchNextPage,
    hasNextContentPage: Boolean(contentQuery.hasNextPage),
    isFetchingNextContentPage: contentQuery.isFetchingNextPage,
    productsRefreshing: Boolean(
      productsQuery.isPreviousData &&
        productsQuery.isFetching &&
        !productsQuery.isFetchingNextPage,
    ),
    contentRefreshing: Boolean(
      contentQuery.isPreviousData &&
        contentQuery.isFetching &&
        !contentQuery.isFetchingNextPage,
    ),
  };
}

/** @deprecated Prefer browseIndexFromProductsFacets. */
export function buildBrowseIndexFromProducts(
  products: ShopCatalogProduct[],
): ShopBrowseIndex {
  return buildShopBrowseIndex(products);
}
